import { useCallback, useEffect, useState, useRef } from 'react';
import { supabase } from './supabaseClient';
import './AdsAdminPage.css';

const ADS_IMAGE_BUCKET = process.env.REACT_APP_SUPABASE_ADS_BUCKET || 'ads-images';

const emptyForm = () => ({
  title: '',
  description: '',
  image_url: '',
  url: '',
});

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function timeAgo(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Az önce';
  if (mins < 60) return `${mins} dk önce`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} saat önce`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} gün önce`;
  return formatDateTime(value);
}

export default function AdsAdminPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const formRef = useRef(null);

  const friendlyError = (msg) => {
    if (/failed to fetch|networkerror|load failed/i.test(msg)) {
      return 'Ağ hatası: İstek sunucuya ulaşamadı. İnternet bağlantınızı kontrol edin.';
    }
    return msg;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: qErr } = await supabase
        .from('promotions')
        .select('id, title, description, image_url, url, created_at, updated_at')
        .order('created_at', { ascending: false });
      if (qErr) setError(friendlyError(qErr.message));
      else setRows(data || []);
    } catch (err) {
      setError(friendlyError(err?.message || 'Veriler yüklenemedi.'));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(false);
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      title: row.title ?? '',
      description: row.description ?? '',
      image_url: row.image_url ?? '',
      url: row.url ?? '',
    });
    setFormOpen(true);
    setError('');
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const title = form.title.trim();
    if (!title) {
      setError('Başlık zorunludur.');
      return;
    }

    const payload = {
      title,
      description: form.description.trim() || null,
      image_url: form.image_url.trim() || null,
      url: form.url.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        const { data, error: uErr } = await supabase
          .from('promotions')
          .update(payload)
          .eq('id', editingId)
          .select('id, title, description, image_url, url, created_at, updated_at')
          .single();
        setSaving(false);
        if (uErr) {
          setError(friendlyError(uErr.message));
          return;
        }
        if (data) {
          setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
          resetForm();
        }
      } else {
        const { data, error: iErr } = await supabase
          .from('promotions')
          .insert(payload)
          .select('id, title, description, image_url, url, created_at, updated_at')
          .single();
        setSaving(false);
        if (iErr) {
          setError(friendlyError(iErr.message));
          return;
        }
        if (data) {
          setRows((prev) => [data, ...prev]);
          resetForm();
        }
      }
    } catch (err) {
      setSaving(false);
      setError(friendlyError(err?.message || 'Bilinmeyen hata oluştu.'));
    }
  };

  const handleImageFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setError('Lutfen yalnizca gorsel dosyasi secin.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Gorsel boyutu en fazla 10MB olabilir.');
      return;
    }

    setUploadingImage(true);
    setError('');

    try {
      const { data: authData, error: authErr } = await supabase.auth.getUser();
      if (authErr) throw authErr;
      const uid = authData?.user?.id || 'anonymous';
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const safeExt = /^[a-z0-9]+$/.test(ext) ? ext : 'jpg';
      const path = `${uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${safeExt}`;

      const { error: uploadErr } = await supabase.storage
        .from(ADS_IMAGE_BUCKET)
        .upload(path, file, { cacheControl: '3600', upsert: false });
      if (uploadErr) throw uploadErr;

      const { data } = supabase.storage.from(ADS_IMAGE_BUCKET).getPublicUrl(path);
      const publicUrl = data?.publicUrl;
      if (!publicUrl) {
        throw new Error('Gorsel yuklendi ama public URL olusturulamadi.');
      }

      setForm((f) => ({ ...f, image_url: publicUrl }));
    } catch (err) {
      const msg = err?.message || 'Gorsel yuklenemedi.';
      if (/bucket/i.test(msg) && /not found/i.test(msg)) {
        setError(`Storage bucket bulunamadi: ${ADS_IMAGE_BUCKET}. Bucket olusturun veya REACT_APP_SUPABASE_ADS_BUCKET degiskenini ayarlayin.`);
      } else if (/row-level security|permission|not allowed|unauthorized|forbidden/i.test(msg)) {
        setError('Gorsel yukleme yetkisi yok. Storage policy (INSERT/SELECT/UPDATE) kontrol edin.');
      } else {
        setError(friendlyError(msg));
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePasteImageUrl = async () => {
    setError('');
    try {
      if (!navigator?.clipboard?.readText) {
        throw new Error('Tarayici panoya erisim izni vermedi.');
      }
      const text = (await navigator.clipboard.readText()).trim();
      if (!text) {
        setError('Panoda bir link bulunamadi.');
        return;
      }
      if (!/^https?:\/\//i.test(text)) {
        setError('Gorsel linki http:// veya https:// ile baslamali.');
        return;
      }
      setForm((f) => ({ ...f, image_url: text }));
    } catch (err) {
      setError(err?.message || 'Panodan link yapistirilamadi.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu reklamı silmek istediğinize emin misiniz?')) return;
    setError('');
    try {
      const { error: dErr } = await supabase.from('promotions').delete().eq('id', id);
      if (dErr) {
        setError(friendlyError(dErr.message));
        return;
      }
    } catch (err) {
      setError(friendlyError(err?.message || 'Silme işlemi başarısız.'));
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== id));
    if (editingId === id) resetForm();
  };

  return (
    <div className="admin-page-content ads-page">
      <div className="ads-header-actions">
        <button type="button" className="ads-refresh-btn" onClick={load} title="Yenile">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <path d="M1 4v6h6" /><path d="M23 20v-6h-6" />
            <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" />
          </svg>
        </button>
        {!formOpen && (
          <button
            type="button"
            className="ads-add-btn"
            onClick={() => { resetForm(); setFormOpen(true); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Yeni Reklam
          </button>
        )}
      </div>

      {/* Stats bar */}
      <div className="ads-stats-bar">
        <div className="ads-stat">
          <span className="ads-stat-value">{rows.length}</span>
          <span className="ads-stat-label">Toplam</span>
        </div>
        <div className="ads-stat">
          <span className="ads-stat-value">{rows.filter((r) => r.image_url).length}</span>
          <span className="ads-stat-label">Görselli</span>
        </div>
        <div className="ads-stat">
          <span className="ads-stat-value">{rows.filter((r) => r.url).length}</span>
          <span className="ads-stat-label">Linkli</span>
        </div>
      </div>

      {/* Form panel */}
      {formOpen && (
        <div className="ads-form-panel" ref={formRef}>
          <div className="ads-form-header">
            <h2>{editingId ? 'Reklamı Düzenle' : 'Yeni Reklam Ekle'}</h2>
            <button type="button" className="ads-form-close" onClick={resetForm}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form className="ads-form" onSubmit={handleSubmit}>
            <div className="ads-form-body">
              <div className="ads-form-fields">
                <div className="ads-field">
                  <label>Başlık *</label>
                  <input
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="Reklam başlığı"
                    required
                  />
                </div>
                <div className="ads-field">
                  <label>Açıklama</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Opsiyonel açıklama metni"
                    rows={3}
                  />
                </div>
                <div className="ads-field">
                  <label>Görsel URL</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      value={form.image_url}
                      onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))}
                      placeholder="https://…"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="ads-refresh-btn"
                      onClick={handlePasteImageUrl}
                      title="Panodaki linki yapistir"
                    >
                      Yapistir
                    </button>
                  </div>
                  <small style={{ display: 'block', marginTop: 6, color: '#64748b' }}>
                    Gorsel linkini buraya yapistirin veya "Yapistir" ile panodan alin.
                  </small>
                </div>
                <div className="ads-field">
                  <label>Gorsel Yukle</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileUpload}
                    disabled={uploadingImage}
                  />
                  <small style={{ display: 'block', marginTop: 6, color: '#64748b' }}>
                    {uploadingImage
                      ? 'Gorsel yukleniyor...'
                      : `Dosya Supabase Storage'a yuklenir (bucket: ${ADS_IMAGE_BUCKET}) ve URL alana otomatik yazilir.`}
                  </small>
                </div>
                <div className="ads-field">
                  <label>Hedef Link</label>
                  <input
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                    placeholder="https://…"
                  />
                </div>
              </div>
              {form.image_url.trim() && (
                <div className="ads-form-preview">
                  <span className="ads-form-preview-label">Önizleme</span>
                  <img
                    src={form.image_url.trim()}
                    alt="Önizleme"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    onLoad={(e) => { e.target.style.display = 'block'; }}
                  />
                </div>
              )}
            </div>
            {error && <p className="ads-error">{error}</p>}
            <div className="ads-form-footer">
              <button type="button" className="ads-cancel-btn" onClick={resetForm}>
                İptal
              </button>
              <button type="submit" className="ads-save-btn" disabled={saving}>
                {saving ? (
                  <>
                    <span className="ads-spinner" />
                    Kaydediliyor…
                  </>
                ) : editingId ? 'Güncelle' : 'Ekle'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Error outside form */}
      {error && !formOpen && <p className="ads-error">{error}</p>}

      {/* Content */}
      {loading ? (
        <div className="ads-loading">
          <div className="ads-loading-dots">
            <span /><span /><span />
          </div>
          <p>Reklamlar yükleniyor…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="ads-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="56" height="56">
            <rect x="3" y="3" width="18" height="14" rx="2" />
            <path d="M7 21h10M12 17v4" />
          </svg>
          <h3>Henüz reklam eklenmemiş</h3>
          <p>Yeni bir reklam ekleyerek başlayın.</p>
          <button
            type="button"
            className="ads-add-btn"
            onClick={() => { resetForm(); setFormOpen(true); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
              <path d="M12 5v14M5 12h14" />
            </svg>
            İlk Reklamı Ekle
          </button>
        </div>
      ) : (
        <div className="ads-grid">
          {rows.map((r, i) => (
            <div
              key={r.id}
              className={`ads-card${editingId === r.id ? ' ads-card-editing' : ''}`}
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <div className="ads-card-image">
                {r.image_url ? (
                  <img src={r.image_url} alt={r.title} />
                ) : (
                  <div className="ads-card-no-image">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  </div>
                )}
                {r.url && (
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ads-card-link-badge"
                    title={r.url}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                      <path d="M15 3h6v6" /><path d="M10 14L21 3" />
                    </svg>
                  </a>
                )}
              </div>
              <div className="ads-card-body">
                <h3 className="ads-card-title">{r.title}</h3>
                {r.description && (
                  <p className="ads-card-desc">{r.description}</p>
                )}
                <span className="ads-card-time" title={formatDateTime(r.created_at)}>
                  {timeAgo(r.created_at)}
                </span>
              </div>
              <div className="ads-card-actions">
                <button type="button" className="ads-card-edit" onClick={() => startEdit(r)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Düzenle
                </button>
                <button type="button" className="ads-card-delete" onClick={() => handleDelete(r.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                    <path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" />
                    <path d="M6 6l1 14a2 2 0 002 2h6a2 2 0 002-2l1-14" />
                  </svg>
                  Sil
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
